import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAccountSwitchOutline, mdiLinkOff } from '@mdi/js'
import { $api } from '../api/queryClient'
import { useAuth } from '../contexts/AuthContext'
import { decodeAccessToken } from '../lib/impersonation'
import { AppIcon } from '../components/ui/icon'
import { Button } from '../components/ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog'

// The page redeeming an impersonation link (spec 2026-10-04-03).
//
// It lives outside routes/$org, so it needs no prior session: a platform admin
// pastes the link in another browser or a private window. The token in the
// path is the credential, valid 1 hour and single use. A session already
// open in this browser is replaced, after a confirm.
export const Route = createFileRoute('/impersonate/$token')({
    component: ImpersonateRoute,
})

type Phase = 'confirm' | 'redeeming' | 'error' | 'cancelled'

function ImpersonateRoute() {
    const { token } = Route.useParams()
    const { t } = useTranslation('admin')
    const navigate = useNavigate()
    const { isAuthenticated, loginWithTokens } = useAuth()
    const redeem = $api.useMutation('post', '/api/v1/auth/impersonate')
    // Decided once, at mount: the session this browser held when the link
    // was opened. It must not flip once the redeemed session is installed.
    const [phase, setPhase] = useState<Phase>(() => (isAuthenticated ? 'confirm' : 'redeeming'))
    // The token works once: never post it twice, even when React runs the
    // mount effect twice in development.
    const started = useRef(false)

    const run = async () => {
        if (started.current) return
        started.current = true
        setPhase('redeeming')
        try {
            const resp = await redeem.mutateAsync({ body: { token } })
            const orgCode = decodeAccessToken(resp.access_token)?.org_code ?? resp.default_org_code ?? ''
            loginWithTokens({
                access_token: resp.access_token ?? '',
                refresh_token: resp.refresh_token ?? '',
                user: resp.user,
                expires_in: resp.expires_in ?? 3600,
                orgCode,
                default_org_code: resp.default_org_code,
                organizations: resp.organizations,
            })
            navigate({ to: '/$org', params: { org: orgCode } })
        } catch {
            setPhase('error')
        }
    }

    useEffect(() => {
        if (phase === 'redeeming') void run()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (phase === 'error') {
        return (
            <div className="max-w-md mx-auto p-6 space-y-4 text-center" data-testid="impersonation-error">
                <AppIcon path={mdiLinkOff} size="lg" className="mx-auto text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">{t('impersonation.invalid_title')}</p>
                <p className="text-sm text-muted-foreground">{t('impersonation.invalid_description')}</p>
                <Button variant="outline" onClick={() => navigate({ to: '/' })} data-testid="impersonation-error-home">
                    {t('impersonation.go_home')}
                </Button>
            </div>
        )
    }

    if (phase === 'cancelled') {
        return (
            <div className="max-w-md mx-auto p-6 space-y-4 text-center" data-testid="impersonation-cancelled">
                <p className="text-sm text-muted-foreground">{t('impersonation.cancelled')}</p>
                <Button variant="outline" onClick={() => navigate({ to: '/' })} data-testid="impersonation-cancelled-home">
                    {t('impersonation.go_home')}
                </Button>
            </div>
        )
    }

    return (
        <div className="max-w-md mx-auto p-6 space-y-4 text-center" data-testid="impersonation-redeeming">
            <AppIcon path={mdiAccountSwitchOutline} size="lg" className="mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t('impersonation.redeeming')}</p>
            <AlertDialog open={phase === 'confirm'}>
                <AlertDialogContent data-testid="impersonation-replace-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('impersonation.replace_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('impersonation.replace_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setPhase('cancelled')} data-testid="impersonation-replace-cancel">
                            {t('impersonation.replace_cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={() => void run()} data-testid="impersonation-replace-confirm">
                            {t('impersonation.replace_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
